import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';
import { useStaticReferrals } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useStaticReferrals';
import { isInvalidSubscription } from '~/utils/isInvalidSubscription';

import { ReferralsContext, type ReferralsContextType } from './ReferralsContext';

type Props = {
  subscription: Subscription;
  publication: Publication;
  subscriberId: string;
  children: React.ReactNode;
};

/**
 * Owns the referrals surface on the General tab — a thin wrapper over `useStaticReferrals`
 * (enabled only when the publication has a referral program and the subscription is valid).
 * Renders no UI; the colocated `ReferralsSection` child renders the `ReferralsElement` from
 * context.
 */
export const ReferralsProvider = ({ subscription, publication, subscriberId, children }: Props) => {
  const showReferrals = !!publication.has_referral_program && !isInvalidSubscription(subscription);

  const {
    isLoading: isLoadingReferrals,
    referrals,
    referralCount,
    referralLink,
    rewards,
    onClickCopy: onClickCopyReferralLink,
  } = useStaticReferrals({
    subscriberId: subscriberId || '',
    enabled: !!subscriberId && showReferrals,
  });

  const value: ReferralsContextType = {
    showReferrals,
    isLoadingReferrals,
    referrals,
    referralCount,
    referralLink,
    rewards,
    onClickCopyReferralLink,
  };

  return <ReferralsContext.Provider value={value}>{children}</ReferralsContext.Provider>;
};
